/* eslint-disable max-len */
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import toUpper from 'lodash/toUpper';

import { IMG_LOGO_CID_TAG } from './companyLogoUtils';
import { COPYRIGHT_YEAR } from './dateutils';
import { Language } from './types/dto-app';

export const DEFAULT_LANGUAGE = Language.EN;
export interface MailTemplate {
  language: string;
  subject: string;
  body: string;
}

export const DEFAULT_CONTACT_NAME = 'Up2Rent';

/** Signatures */
const SIGNATURES = {
  [Language.EN]: `
      <p></p>
      <p style="height: 1px;margin: 0 0 16px;background-color: #ededf2;"></p>
      <p><span style="font-family: Arial;font-size: 10px;font-weight: normal;font-stretch: normal;font-style: normal;line-height: 1.33;letter-spacing: normal;color: #607d8b">
      This email was sent to you as a registered user of <a style="font-family: Arial;font-size: 10px;font-weight: normal;font-stretch: normal;font-style: normal;  line-height: 1.33; letter-spacing: normal; color: #4b7bec" href='https://www.up2rent.com' target='_blank'>Up2Rent</a>.
      <br />© ${COPYRIGHT_YEAR} Up2Rent, a brand of Aloalto SA, Eikelenbergstraat 20, 1700 Dilbeek, Belgium. All rights reserved.</span></p>`,
  [Language.FR]: `<p></p>
      <p style="height: 1px;margin: 0 0 16px;background-color: #ededf2;"></p>
      <p><span style="font-family: Arial;font-size: 10px;font-weight: normal;font-stretch: normal;font-style: normal;line-height: 1.33;letter-spacing: normal;color: #607d8b">
      Cet e-mail vous a été envoyé en tant qu'utilisateur d'<a style="font-family: Arial;font-size: 10px;font-weight: normal;font-stretch: normal;font-style: normal;  line-height: 1.33; letter-spacing: normal; color: #4b7bec" href='https://www.up2rent.com' target='_blank'>Up2Rent</a>.
      <br />© ${COPYRIGHT_YEAR} Up2Rent, une marque Aloalto SA, Eikelenbergstraat 20, 1700 Dilbeek, Belgique. Tous droits réservés.</span></p>`,
  [Language.NL]: `<p></p>
      <p style="height: 1px;margin: 0 0 16px;background-color: #ededf2;"></p>
      <p><span style="font-family: Arial;font-size: 10px;font-weight: normal;font-stretch: normal;font-style: normal;line-height: 1.33;letter-spacing: normal;color: #607d8b">
      Deze e-mail werd u toegezonden als geregistreerd gebruiker van <a style="font-family: Arial;font-size: 10px;font-weight: normal;font-stretch: normal;font-style: normal;  line-height: 1.33; letter-spacing: normal; color: #4b7bec" href='https://www.up2rent.com' target='_blank'>Up2Rent</a>.
      <br />© ${COPYRIGHT_YEAR} Up2Rent, een merk van Aloalto NV, Eikelenbergstraat 20, 1700 Dilbeek, België. Alle rechten voorbehouden.</span></p>`,
  [Language.DE]: `<p></p>
      <p style="height: 1px;margin: 0 0 16px;background-color: #ededf2;"></p>
      <p><span style="font-family: Arial;font-size: 10px;font-weight: normal;font-stretch: normal;font-style: normal;line-height: 1.33;letter-spacing: normal;color: #607d8b">
      Diese E-Mail wurde Ihnen als registrierter Benutzer von <a style="font-family: Arial;font-size: 10px;font-weight: normal;font-stretch: normal;font-style: normal;  line-height: 1.33; letter-spacing: normal; color: #4b7bec" href='https://www.up2rent.com' target='_blank'>Up2Rent</a> zugesandt.
      <br />© ${COPYRIGHT_YEAR} Up2Rent, eine Marke von Aloalto SA, Eikelenbergstraat 20, 1700 Dilbeek, Belgien. Alle Rechte vorbehalten.</span></p>`,
};

/** Conditions and policy */

const getUrlForLegalFileName = (fileName: string, language?: string | null) => {
  return `http://up2rent.com/wp-content/uploads/${fileName}${toUpper(language ?? DEFAULT_LANGUAGE)}.pdf`;
};

const legalDocuments = [
  { fileName: 'Up2Rent_Privacy_Policy_', labelId: 'signup.privacyPolicy' },
  { fileName: 'Up2Rent_Enterprise_Subscription_Agreement_', labelId: 'signup.generalConditions' },
  { fileName: 'Up2Rent_Acceptable_Use_Policy_', labelId: 'signup.acceptableUsePolicy' },
  { fileName: 'Up2Rent_General_Data_Protection_Regulation_', labelId: 'signup.gdpr' },
  {
    fileName: 'Up2Rent_Responsible_Disclosure_of_Up2Rent_Security_Vulnerabilities_',
    labelId: 'signup.securityVulnerabilities',
  },
  { fileName: 'Up2Rent_Security_', labelId: 'signup.security' },
  { fileName: 'Up2Rent_Service_Level_Agreement_', labelId: 'signup.sla' },
];

export const getPrivacyPolicyUrl = (language?: string | null) =>
  getUrlForLegalFileName(legalDocuments[0].fileName, language);

export const getGeneralConditionsUrl = (language?: string | null) =>
  getUrlForLegalFileName(legalDocuments[1].fileName, language);

export const getLegalDocuments = (language?: string | null) => {
  return legalDocuments.map(({ fileName, labelId }) => ({ url: getUrlForLegalFileName(fileName, language), labelId }));
};

/** Email templates */
const linkAccountMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: New login method',
    body: `<p>Dear {{firstName}} {{lastName}},</p>
        <p></p>
        <p>A new login method has been linked to your Up2Rent account.</p>
        <ul>
        <li>Provider: <strong>{{provider}}</strong></li>
        <li>Email: <strong>{{email}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Nieuwe inlogmethode',
    body: `
        <p>Geachte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Er werd een nieuwe inlogmethode aan uw Up2Rent account gekoppeld.</p>
        <ul>
        <li>Provider: <strong>{{provider}}</strong></li>
        <li>Email: <strong>{{email}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Nouvelle méthode de connexion',
    body: `<p>Cher {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Une nouvelle méthode de connexion a été ajoutée à votre compte Up2Rent.</p>
        <ul>
        <li>Réseau: <strong>{{provider}}</strong></li>
        <li>Email: <strong>{{email}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Neue Anmeldemethode',
    body: `<p>Sehr geehrte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Eine neue Anmeldemethode wurde mit Ihrem Up2Rent-Konto verknüpft.</p>
        <ul>
        <li>Provider: <strong>{{provider}}</strong></li>
        <li>Email: <strong>{{email}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const signupMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: New user account',
    body: `<p>Dear {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Your Up2Rent user account has just been created. To confirm your email address, please click the link below:<br /><a href="{{verificationLink}}">Verify your email address</a></p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Nieuwe gebruikersaccount',
    body: `
        <p>Geachte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Uw Up2Rent gebruikersaccount is zojuist aangemaakt. Om de geldigheid van uw email te bevestigen, klik op de onderstaande link:<br /><a href="{{verificationLink}}">Bevestig uw emailadres</a></p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Nouveau compte utilisateur',
    body: `<p>Cher/Chère {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Votre compte utilisateur Up2Rent vient d'être créé. Afin de confirmer la validité de votre adresse email, veuillez cliquer sur le lien ci-dessous :<br /><a href="{{verificationLink}}">Vérifier votre adresse email</a></p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Neues Benutzerkonto',
    body: `<p>Sehr geehrte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Ihr Up2Rent-Benutzerkonto wurde gerade erstellt. Um die Gültigkeit Ihrer E-Mail-Adresse zu bestätigen, klicken Sie bitte auf den folgenden Link:<br /><a href="{{verificationLink}}">E-Mail-Adresse bestätigen</a></p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const forgotPasswordMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Forgot Password',
    body: `<p>Dear {{firstName}} {{lastName}},</p>
        <p></p>
        <p>You have requested a password reset of your Up2Rent account.</p>
        <p>To reset your password, please click the link below:</p>
        <p><a href="{{resetLink}}">Reset your password</a></p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Wachtwoord vergeten',
    body: `
        <p>Geachte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>U heeft een nieuw wachtwoord aangevraagd voor uw Up2Rent account.</p>
        <p>Om uw wachtwoord te resetten, klik op de onderstaande link:</p>
        <p><a href="{{resetLink}}">Wachtwoord resetten</a></p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Mot de passe oublié',
    body: `<p>Cher/Chère {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Vous avez demandé une réinitialisation de votre mot de passe Up2Rent.</p>
        <p>Pour réinitialiser votre mot de passe, veuillez cliquer sur le lien ci-dessous :</p>
        <p><a href="{{resetLink}}">Réinitialiser votre mot de passe</a></p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Passwort vergessen',
    body: `<p>Sehr geehrte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Sie haben eine Passwortzurücksetzung für Ihr Up2Rent-Konto angefordert.</p>
        <p>Um Ihr Passwort zurückzusetzen, klicken Sie bitte auf den folgenden Link:</p>
        <p><a href="{{resetLink}}">Passwort zurücksetzen</a></p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const resendVerificationCodeMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Confirmation code',
    body: `<p>Dear {{firstName}} {{lastName}},</p>
        <p></p>
        <p>You have requested a new verification email. Please click the link below to verify your email address:</p>
        <p><a href="{{verificationLink}}">Verify your email address</a></p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Bevestigingscode',
    body: `
        <p>Geachte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>U heeft een nieuwe verificatie-email aangevraagd. Klik op de onderstaande link om uw emailadres te verifiëren:</p>
        <p><a href="{{verificationLink}}">Bevestig uw emailadres</a></p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Code de confirmation',
    body: `<p>Cher/Chère {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Vous avez demandé l'envoi d'un nouvel email de vérification. Veuillez cliquer sur le lien ci-dessous pour vérifier votre adresse email :</p>
        <p><a href="{{verificationLink}}">Vérifier votre adresse email</a></p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Bestätigungscode',
    body: `<p>Sehr geehrte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Sie haben eine neue Bestätigungs-E-Mail angefordert. Bitte klicken Sie auf den folgenden Link, um Ihre E-Mail-Adresse zu bestätigen:</p>
        <p><a href="{{verificationLink}}">E-Mail-Adresse bestätigen</a></p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const signInMagicLinkMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Sign-in link',
    body: `<p>Hello,</p>
        <p></p>
        <p>You requested a link to sign in to Up2Rent. Click below to log in — the link expires soon and can only be used once.</p>
        <p><a href="{{magicLink}}">Sign in to Up2Rent</a></p>
        <p>If you didn't request this, you can safely ignore this email.</p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Inloglink',
    body: `<p>Hallo,</p>
        <p></p>
        <p>U heeft een link aangevraagd om in te loggen op Up2Rent. Klik hieronder om in te loggen — de link verloopt snel en kan slechts één keer worden gebruikt.</p>
        <p><a href="{{magicLink}}">Inloggen bij Up2Rent</a></p>
        <p>Als u dit niet heeft aangevraagd, kunt u deze e-mail negeren.</p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Lien de connexion',
    body: `<p>Bonjour,</p>
        <p></p>
        <p>Vous avez demandé un lien pour vous connecter à Up2Rent. Cliquez ci-dessous pour vous connecter — le lien expire bientôt et ne peut être utilisé qu'une seule fois.</p>
        <p><a href="{{magicLink}}">Se connecter à Up2Rent</a></p>
        <p>Si vous n'êtes pas à l'origine de cette demande, vous pouvez ignorer cet email.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Anmeldelink',
    body: `<p>Hallo,</p>
        <p></p>
        <p>Sie haben einen Link zur Anmeldung bei Up2Rent angefordert. Klicken Sie unten, um sich anzumelden — der Link läuft bald ab und kann nur einmal verwendet werden.</p>
        <p><a href="{{magicLink}}">Bei Up2Rent anmelden</a></p>
        <p>Wenn Sie dies nicht angefordert haben, können Sie diese E-Mail ignorieren.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const accountDeletionRequestedMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Your account deletion request',
    body: `<p>Hello {{firstName}},</p>
        <p></p>
        <p>We received your request to delete your Up2Rent account. Your account and your access to all workspaces will be permanently deleted on <strong>{{purgeDate}}</strong>.</p>
        <p>Until then, you can sign in and choose "Restore my account" to cancel the deletion.</p>
        <p>If you own a workspace with no other members, that workspace will be closed too. Restoring your account later does not reopen it; contact support if you need it back.</p>
        <p>If you did not request this, sign in now, restore your account, and change your password.</p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Uw verzoek tot verwijdering van uw account',
    body: `<p>Hallo {{firstName}},</p>
        <p></p>
        <p>We hebben uw verzoek ontvangen om uw Up2Rent-account te verwijderen. Uw account en uw toegang tot alle werkruimtes worden definitief verwijderd op <strong>{{purgeDate}}</strong>.</p>
        <p>Tot die tijd kunt u inloggen en "Mijn account herstellen" kiezen om de verwijdering te annuleren.</p>
        <p>Als u eigenaar bent van een werkruimte zonder andere leden, wordt die werkruimte ook gesloten. Als u uw account later herstelt, wordt deze niet opnieuw geopend; neem contact op met support als u deze terug wilt.</p>
        <p>Als u dit niet heeft aangevraagd, log dan nu in, herstel uw account en wijzig uw wachtwoord.</p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Votre demande de suppression de compte',
    body: `<p>Bonjour {{firstName}},</p>
        <p></p>
        <p>Nous avons bien reçu votre demande de suppression de votre compte Up2Rent. Votre compte et votre accès à tous les espaces de travail seront définitivement supprimés le <strong>{{purgeDate}}</strong>.</p>
        <p>D'ici là, vous pouvez vous connecter et choisir "Restaurer mon compte" pour annuler la suppression.</p>
        <p>Si vous êtes propriétaire d'un espace de travail sans autre membre, cet espace sera également fermé. Restaurer votre compte ultérieurement ne le rouvre pas ; contactez le support si vous souhaitez le récupérer.</p>
        <p>Si vous n'êtes pas à l'origine de cette demande, connectez-vous dès maintenant, restaurez votre compte et changez votre mot de passe.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Ihre Anfrage zur Kontolöschung',
    body: `<p>Hallo {{firstName}},</p>
        <p></p>
        <p>Wir haben Ihre Anfrage zur Löschung Ihres Up2Rent-Kontos erhalten. Ihr Konto und Ihr Zugriff auf alle Arbeitsbereiche werden am <strong>{{purgeDate}}</strong> endgültig gelöscht.</p>
        <p>Bis dahin können Sie sich anmelden und "Mein Konto wiederherstellen" wählen, um die Löschung abzubrechen.</p>
        <p>Wenn Sie Eigentümer eines Arbeitsbereichs ohne weitere Mitglieder sind, wird dieser Arbeitsbereich ebenfalls geschlossen. Das spätere Wiederherstellen Ihres Kontos öffnet ihn nicht erneut; wenden Sie sich an den Support, wenn Sie ihn zurückhaben möchten.</p>
        <p>Wenn Sie dies nicht angefordert haben, melden Sie sich jetzt an, stellen Sie Ihr Konto wieder her und ändern Sie Ihr Passwort.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const ownershipReceivedMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: You are now the owner of {{workspaceName}}',
    body: `<p>Hello {{firstName}},</p>
        <p></p>
        <p>{{previousRootName}} has transferred ownership of the workspace <strong>{{workspaceName}}</strong> to you.</p>
        <p>You are now the owner of this workspace and responsible for its account.</p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: U bent nu de eigenaar van {{workspaceName}}',
    body: `<p>Hallo {{firstName}},</p>
        <p></p>
        <p>{{previousRootName}} heeft de eigendom van de werkruimte <strong>{{workspaceName}}</strong> aan u overgedragen.</p>
        <p>U bent nu de eigenaar van deze werkruimte en verantwoordelijk voor het account.</p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Vous êtes désormais propriétaire de {{workspaceName}}',
    body: `<p>Bonjour {{firstName}},</p>
        <p></p>
        <p>{{previousRootName}} vous a transféré la propriété de l'espace de travail <strong>{{workspaceName}}</strong>.</p>
        <p>Vous êtes désormais propriétaire de cet espace de travail et responsable de son compte.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Sie sind jetzt Eigentümer von {{workspaceName}}',
    body: `<p>Hallo {{firstName}},</p>
        <p></p>
        <p>{{previousRootName}} hat Ihnen die Eigentümerschaft des Arbeitsbereichs <strong>{{workspaceName}}</strong> übertragen.</p>
        <p>Sie sind nun Eigentümer dieses Arbeitsbereichs und für dessen Konto verantwortlich.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const ownershipGivenMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Ownership of {{workspaceName}} transferred',
    body: `<p>Hello {{firstName}},</p>
        <p></p>
        <p>You have transferred ownership of the workspace <strong>{{workspaceName}}</strong> to {{newRootName}}.</p>
        <p>{{newRootName}} is now the owner of this workspace and responsible for its account.</p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Eigendom van {{workspaceName}} overgedragen',
    body: `<p>Hallo {{firstName}},</p>
        <p></p>
        <p>U heeft de eigendom van de werkruimte <strong>{{workspaceName}}</strong> overgedragen aan {{newRootName}}.</p>
        <p>{{newRootName}} is nu de eigenaar van deze werkruimte en verantwoordelijk voor het account.</p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Propriété de {{workspaceName}} transférée',
    body: `<p>Bonjour {{firstName}},</p>
        <p></p>
        <p>Vous avez transféré la propriété de l'espace de travail <strong>{{workspaceName}}</strong> à {{newRootName}}.</p>
        <p>{{newRootName}} est désormais propriétaire de cet espace de travail et responsable de son compte.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Eigentümerschaft von {{workspaceName}} übertragen',
    body: `<p>Hallo {{firstName}},</p>
        <p></p>
        <p>Sie haben die Eigentümerschaft des Arbeitsbereichs <strong>{{workspaceName}}</strong> an {{newRootName}} übertragen.</p>
        <p>{{newRootName}} ist nun Eigentümer dieses Arbeitsbereichs und für dessen Konto verantwortlich.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const inviteMemberToAccountMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: '{{getContactName client}} invites you on Up2Rent',
    body: `<p>Hello,</p>
        <p></p>
        <p>You are invited as a new member of your organisation's Up2Rent account. Please use the link below to create your account:</p>
        <ul>
        <li>Registration link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: '{{getContactName client}} nodigt u uit op Up2Rent',
    body: `
        <p>Hallo,</p>
        <p></p>
        <p>U bent uitgenodigd als nieuw lid van de Up2Rent-account van uw organisatie. Gebruik de onderstaande link om uw account aan te maken:</p>
        <ul>
        <li>Registratielink: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: '{{getContactName client}} vous invite sur Up2Rent',
    body: `<p>Bonjour,</p>
        <p></p>
        <p>Vous êtes invité en tant que nouveau membre du compte Up2Rent de votre organisation. Veuillez utiliser le lien ci dessous pour créer votre compte:</p>
        <ul>
        <li>Lien d'enregistrement: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: '{{getContactName client}} lädt Sie zu Up2Rent ein',
    body: `<p>Hallo,</p>
        <p></p>
        <p>Sie sind eingeladen, neues Mitglied des Up2Rent-Kontos Ihrer Organisation zu werden. Bitte verwenden Sie den folgenden Link, um Ihr Konto zu erstellen:</p>
        <ul>
        <li>Registrierungslink: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const reminderInviteMemberToAccountMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Reminder: {{getContactName client}} invites you on Up2Rent',
    body: `<p>Hello,</p>
        <p></p>
        <p>This is a friendly reminder that you have been invited as a new member of your organisation's Up2Rent account. Please use the link below to create your account:</p>
        <ul>
        <li>Registration link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Herinnering: {{getContactName client}} nodigt u uit op Up2Rent',
    body: `
        <p>Hallo,</p>
        <p></p>
        <p>Dit is een vriendelijke herinnering dat u bent uitgenodigd als nieuw lid van de Up2Rent-account van uw organisatie. Gebruik de onderstaande link om uw account aan te maken:</p>
        <ul>
        <li>Registratielink: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Rappel: {{getContactName client}} vous invite sur Up2Rent',
    body: `<p>Bonjour,</p>
        <p></p>
        <p>Ceci est un rappel amical que vous êtes invité en tant que nouveau membre du compte Up2Rent de votre organisation. Veuillez utiliser le lien ci-dessous pour créer votre compte:</p>
        <ul>
        <li>Lien d'enregistrement: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Erinnerung: {{getContactName client}} lädt Sie zu Up2Rent ein',
    body: `<p>Hallo,</p>
        <p></p>
        <p>Dies ist eine freundliche Erinnerung, dass Sie als neues Mitglied des Up2Rent-Kontos Ihrer Organisation eingeladen wurden. Bitte verwenden Sie den folgenden Link, um Ihr Konto zu erstellen:</p>
        <ul>
        <li>Registrierungslink: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const inviteOwnerToAccountMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: '{{getContactName client}} invites you to visualize your real estate on Up2Rent',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>{{getContactName client}} invites you to activate your Up2Rent account in order to follow the management of your real estate. Please use the link below to activate it:</p>
        <ul>
        <li>Activation link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: '{{getContactName client}} nodigt u uit om uw vastgoed te visualiseren op Up2Rent',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>{{getContactName client}} nodigt u uit om uw Up2Rent account te activeren om het beheer van uw vastgoed te volgen. Gebruik de onderstaande link om het te activeren:</p>
        <ul>
        <li>Activatie link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: '{{getContactName client}} vous invite à visualiser vos biens immobiliers sur Up2Rent',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>{{getContactName client}} vous invite à activer votre compte Up2Rent afin de pouvoir suivre la gestion de votre patrimoine immobilier. Veuillez utiliser le lien ci-dessous pour l'activer:</p>
        <ul>
        <li>Lien d'activation: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: '{{getContactName client}} lädt Sie ein, Ihre Immobilien auf Up2Rent zu visualisieren',
    body: `<p>Hallo {{getContactName contact}},</p>
        <p></p>
        <p>{{getContactName client}} lädt Sie ein, Ihr Up2Rent-Konto zu aktivieren, um die Verwaltung Ihrer Immobilien zu verfolgen. Bitte verwenden Sie den folgenden Link, um es zu aktivieren:</p>
        <ul>
        <li>Aktivierungslink: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const reminderInviteOwnerToAccountMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Reminder: {{getContactName client}} invites you to visualize your real estate on Up2Rent',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>This is a friendly reminder that {{getContactName client}} invites you to activate your Up2Rent account in order to follow the management of your real estate. Please use the link below to activate it:</p>
        <ul>
        <li>Activation link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Herinnering: {{getContactName client}} nodigt u uit om uw vastgoed te visualiseren op Up2Rent',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Dit is een vriendelijke herinnering dat {{getContactName client}} u uitnodigt om uw Up2Rent account te activeren om het beheer van uw vastgoed te volgen. Gebruik de onderstaande link om het te activeren:</p>
        <ul>
        <li>Activatie link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Rappel: {{getContactName client}} vous invite à visualiser vos biens immobiliers sur Up2Rent',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Ceci est un rappel amical que {{getContactName client}} vous invite à activer votre compte Up2Rent afin de pouvoir suivre la gestion de votre patrimoine immobilier. Veuillez utiliser le lien ci-dessous pour l'activer:</p>
        <ul>
        <li>Lien d'activation: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Erinnerung: {{getContactName client}} lädt Sie ein, Ihre Immobilien auf Up2Rent zu visualisieren',
    body: `<p>Hallo {{getContactName contact}},</p>
        <p></p>
        <p>Dies ist eine freundliche Erinnerung, dass {{getContactName client}} Sie einlädt, Ihr Up2Rent-Konto zu aktivieren, um die Verwaltung Ihrer Immobilien zu verfolgen. Bitte verwenden Sie den folgenden Link, um es zu aktivieren:</p>
        <ul>
        <li>Aktivierungslink: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const inviteTenantToAccountMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: '{{getContactName client}} invites you to visualize your lease on Up2Rent',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>A lease in your name has been created on Up2Rent by {{getContactName client}} and an account has been created automatically for you. Please use the link below to activate your account:</p>
        <ul>
        <li>Activation link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: '{{getContactName client}} heeft je uitgenodigd bij Up2Rent',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Een huurcontract op uw naam is aangemaakt op Up2Rent door {{getContactName client}} en er werd automatisch een account voor u aangemaakt. Gebruik onderstaande link om uw account te activeren:</p>
        <ul>
        <li>Activatie link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: '{{getContactName client}} vous invite à voir votre bail sur Up2Rent',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Un bail a votre nom a été créé sur Up2Rent par {{getContactName client}} et un compte a été créé automatiquement pour vous. Veuillez utiliser le lien ci-dessous pour activer votre compte:</p>
        <ul>
        <li>Lien d'activation: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: '{{getContactName client}} lädt Sie ein, Ihren Mietvertrag auf Up2Rent zu visualisieren',
    body: `<p>Hallo {{getContactName contact}},</p>
        <p></p>
        <p>Ein Mietvertrag auf Ihren Namen wurde von {{getContactName client}} auf Up2Rent erstellt und ein Konto wurde automatisch für Sie erstellt. Bitte verwenden Sie den folgenden Link, um Ihr Konto zu aktivieren:</p>
        <ul>
        <li>Aktivierungslink: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const reminderInviteTenantToAccountMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Reminder: {{getContactName client}} invites you to visualize your lease on Up2Rent',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>This is a friendly reminder that a lease in your name has been created on Up2Rent by {{getContactName client}} and an account has been created automatically for you. Please use the link below to activate your account:</p>
        <ul>
        <li>Activation link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Herinnering: {{getContactName client}} heeft je uitgenodigd bij Up2Rent',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Dit is een vriendelijke herinnering dat een huurcontract op uw naam is aangemaakt op Up2Rent door {{getContactName client}} en er werd automatisch een account voor u aangemaakt. Gebruik onderstaande link om uw account te activeren:</p>
        <ul>
        <li>Activatie link: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Rappel: {{getContactName client}} vous invite à voir votre bail sur Up2Rent',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Ceci est un rappel amical qu'un bail a votre nom a été créé sur Up2Rent par {{getContactName client}} et un compte a été créé automatiquement pour vous. Veuillez utiliser le lien ci-dessous pour activer votre compte:</p>
        <ul>
        <li>Lien d'activation: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Erinnerung: {{getContactName client}} lädt Sie ein, Ihren Mietvertrag auf Up2Rent zu visualisieren',
    body: `<p>Hallo {{getContactName contact}},</p>
        <p></p>
        <p>Dies ist eine freundliche Erinnerung, dass ein Mietvertrag auf Ihren Namen von {{getContactName client}} auf Up2Rent erstellt wurde und ein Konto automatisch für Sie erstellt wurde. Bitte verwenden Sie den folgenden Link, um Ihr Konto zu aktivieren:</p>
        <ul>
        <li>Aktivierungslink: <strong>{{registrationLink}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const inviteContactToSignLeaseMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: '{{getContactName client}} invites you to read and sign your rental lease on Up2Rent',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>A new lease contract has been created on Up2Rent by {{getContactName client}}. In order to review and sign the contractual document(s), we kindly invite you to activate your Up2Rent account by following the link below:</p>
        <ul>
        <li>Activation link: <strong>{{registrationLink}}</strong></li>
        <li>Comment by the author: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: '{{getContactName client}} nodigt u uit om uw huurovereenkomst op Up2Rent te lezen en te ondertekenen',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Er is een nieuw huurcontract aangemaakt op Up2Rent door {{getContactName client}}. Om het (de) contractuele document(en) te kunnen bekijken en ondertekenen, nodigen wij u uit om een Up2Rent account aan te maken door de onderstaande link te volgen:</p>
        <ul>
        <li>Activatie link: <strong>{{registrationLink}}</strong></li>
        <li>Commentaar van de auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: '{{getContactName client}} vous invite à lire et signer votre bail locatif sur Up2Rent.',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Un bail a votre nom a été créé sur Up2Rent par {{getContactName client}}. Afin de pouvoir lire et signer le(s) document(s) contractuel(s), nous vous invitons à activer votre compte Up2Rent en suivant le lien ci-dessous:</p>
        <ul>
        <li>Lien d'activation: <strong>{{registrationLink}}</strong></li>
        <li>Commentaire de l'auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: '{{getContactName client}} lädt Sie ein, Ihren Mietvertrag auf Up2Rent zu lesen und zu unterschreiben',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Ein neuer Mietvertrag wurde auf Up2Rent von {{getContactName client}} erstellt. Um die Vertragsdokumente zu prüfen und zu unterschreiben, laden wir Sie ein, Ihr Up2Rent-Konto zu aktivieren, indem Sie dem untenstehenden Link folgen:</p>
        <ul>
        <li>Aktivierungslink: <strong>{{registrationLink}}</strong></li>
        <li>Kommentar des Autors: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const reminderInviteContactToSignLeaseMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Reminder: {{getContactName client}} invites you to read and sign your rental lease on Up2Rent',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>This is a friendly reminder that a new lease contract has been created on Up2Rent by {{getContactName client}}. In order to review and sign the contractual document(s), we kindly invite you to activate your Up2Rent account by following the link below:</p>
        <ul>
        <li>Activation link: <strong>{{registrationLink}}</strong></li>
        <li>Comment by the author: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject:
      'Herinnering: {{getContactName client}} nodigt u uit om uw huurovereenkomst op Up2Rent te lezen en te ondertekenen',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Dit is een vriendelijke herinnering dat er een nieuw huurcontract is aangemaakt op Up2Rent door {{getContactName client}}. Om het (de) contractuele document(en) te kunnen bekijken en ondertekenen, nodigen wij u uit om een Up2Rent account aan te maken door de onderstaande link te volgen:</p>
        <ul>
        <li>Activatie link: <strong>{{registrationLink}}</strong></li>
        <li>Commentaar van de auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Rappel: {{getContactName client}} vous invite à lire et signer votre bail locatif sur Up2Rent.',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Ceci est un rappel amical qu'un bail a votre nom a été créé sur Up2Rent par {{getContactName client}}. Afin de pouvoir lire et signer le(s) document(s) contractuel(s), nous vous invitons à activer votre compte Up2Rent en suivant le lien ci-dessous:</p>
        <ul>
        <li>Lien d'activation: <strong>{{registrationLink}}</strong></li>
        <li>Commentaire de l'auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject:
      'Erinnerung: {{getContactName client}} lädt Sie ein, Ihren Mietvertrag auf Up2Rent zu lesen und zu unterschreiben',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Dies ist eine freundliche Erinnerung, dass ein neuer Mietvertrag auf Up2Rent von {{getContactName client}} erstellt wurde. Um die Vertragsdokumente zu prüfen und zu unterschreiben, laden wir Sie ein, Ihr Up2Rent-Konto zu aktivieren, indem Sie dem untenstehenden Link folgen:</p>
        <ul>
        <li>Aktivierungslink: <strong>{{registrationLink}}</strong></li>
        <li>Kommentar des Autors: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const inviteContactToAccountForDocumentMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Invite to sign document(s)',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>One or more new documents have been added to your lease. In order to review and sign the contractual document(s), we kindly invite you to create an Up2Rent account by following the link below:</p>
        <ul>
        <li>Registration link: <strong>{{registrationLink}}</strong></li>
        <li>Comment by the author: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Uitnodigen tot het ondertekenen van document(en)',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Er zijn één of meer nieuwe documenten aan uw huurcontract toegevoegd. Om deze contractuele documenten te kunnen bekijken en te ondertekenen, nodigen wij u uit om een Up2Rent account aan te maken door de onderstaande link te volgen:</p>
        <ul>
        <li>Registratielink: <strong>{{registrationLink}}</strong></li>
        <li>Commentaar van de auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Invitation a signer votre document',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Un ou plusieurs nouveaux documents ont été ajoutés à votre bail. Afin de pouvoir examiner et signer ces documents, nous vous invitons à créer un compte Up2Rent en suivant le lien ci-dessous:</p>
        <ul>
        <li>Lien d'enregistrement: <strong>{{registrationLink}}</strong></li>
        <li>Commentaire de l'auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Einladung zur Unterzeichnung von Dokument(en)',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Ein oder mehrere neue Dokumente wurden Ihrem Mietvertrag hinzugefügt. Um diese Vertragsdokumente zu prüfen und zu unterschreiben, laden wir Sie ein, ein Up2Rent-Konto zu erstellen, indem Sie dem untenstehenden Link folgen:</p>
        <ul>
        <li>Registrierungslink: <strong>{{registrationLink}}</strong></li>
        <li>Kommentar des Autors: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const reminderInviteContactToAccountForDocumentMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Reminder Up2Rent: Invite to sign document(s)',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>This is a friendly reminder that one or more new documents have been added to your lease. In order to review and sign the contractual document(s), we kindly invite you to create an Up2Rent account by following the link below:</p>
        <ul>
        <li>Registration link: <strong>{{registrationLink}}</strong></li>
        <li>Comment by the author: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Herinnering Up2Rent: Uitnodigen tot het ondertekenen van document(en)',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Dit is een vriendelijke herinnering dat er één of meer nieuwe documenten aan uw huurcontract zijn toegevoegd. Om deze contractuele documenten te kunnen bekijken en te ondertekenen, nodigen wij u uit om een Up2Rent account aan te maken door de onderstaande link te volgen:</p>
        <ul>
        <li>Registratielink: <strong>{{registrationLink}}</strong></li>
        <li>Commentaar van de auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Rappel Up2Rent: Invitation a signer votre document',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Ceci est un rappel amical qu'un ou plusieurs nouveaux documents ont été ajoutés à votre bail. Afin de pouvoir examiner et signer ces documents, nous vous invitons à créer un compte Up2Rent en suivant le lien ci-dessous:</p>
        <ul>
        <li>Lien d'enregistrement: <strong>{{registrationLink}}</strong></li>
        <li>Commentaire de l'auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Erinnerung Up2Rent: Einladung zur Unterzeichnung von Dokument(en)',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Dies ist eine freundliche Erinnerung, dass ein oder mehrere neue Dokumente Ihrem Mietvertrag hinzugefügt wurden. Um diese Vertragsdokumente zu prüfen und zu unterschreiben, laden wir Sie ein, ein Up2Rent-Konto zu erstellen, indem Sie dem untenstehenden Link folgen:</p>
        <ul>
        <li>Registrierungslink: <strong>{{registrationLink}}</strong></li>
        <li>Kommentar des Autors: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const newLeaseVersionMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Invite to sign your lease',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>A new lease contract has been created. In order to review and sign the contractual document(s), please use the link below:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Review and Sign Documents</a></strong></li>
        <li>Comment by the author: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Uitnodigen tot het ondertekenen van uw huurovereenkomst',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Er is een nieuw huurcontract aangemaakt. Om het (de) contractuele document(en) te bekijken en te ondertekenen, gebruik de onderstaande link:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Documenten bekijken en ondertekenen</a></strong></li>
        <li>Commentaar van de auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Invitation a signer votre bail',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Un nouveau contrat de bail a été créé. Afin d'examiner et de signer le(s) document(s) contractuel(s), veuillez utiliser le lien ci-dessous:</p>
        <ul>
        <li>Lien: <strong><a href="{{connectionLink}}" target="_blank">Consulter et signer les documents</a></strong></li>
        <li>Commentaire de l'auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Einladung zur Unterzeichnung Ihres Mietvertrags',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Ein neuer Mietvertrag wurde erstellt. Um die Vertragsdokumente zu prüfen und zu unterschreiben, verwenden Sie bitte den folgenden Link:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Dokumente ansehen und unterschreiben</a></strong></li>
        <li>Kommentar des Autors: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const documentSignatureReminderMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Reminder to sign your document',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>This is a friendly reminder that you have documents pending your review and signature. Please use the link below to access them:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Review and Sign Documents</a></strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Herinnering om uw document te ondertekenen',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Dit is een vriendelijke herinnering dat u documenten heeft die wachten op uw beoordeling en handtekening. Gebruik de onderstaande link om ze te openen:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Documenten bekijken en ondertekenen</a></strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Rappel pour signer votre document',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Ceci est un rappel amical que vous avez des documents en attente de votre relecture et signature. Veuillez utiliser le lien ci-dessous pour y accéder:</p>
        <ul>
        <li>Lien: <strong><a href="{{connectionLink}}" target="_blank">Consulter et signer les documents</a></strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Erinnerung, Ihr Dokument zu unterschreiben',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Dies ist eine freundliche Erinnerung, dass Sie Dokumente haben, die auf Ihre Überprüfung und Unterschrift warten. Bitte verwenden Sie den untenstehenden Link, um darauf zuzugreifen:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Dokumente ansehen und unterschreiben</a></strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const newDocumentMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Invite to sign your document',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>One or more new documents have been added to sign. In order to review and sign these documents, use the following link:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Review and Sign Documents</a></strong></li>
        <li>Comment by the author: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Uitnodigen tot het ondertekenen van uw document',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Er zijn één of meer nieuwe documenten toegevoegd om te ondertekenen. Om deze documenten te bekijken en te ondertekenen, gebruik de onderstaande link:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Documenten bekijken en ondertekenen</a></strong></li>
        <li>Commentaar van de auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Invitation a signer votre document',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Un ou plusieurs nouveaux documents ont été ajoutés pour signature. Pour consulter et signer ces documents, utilisez le lien ci-dessous:</p>
        <ul>
        <li>Lien: <strong><a href="{{connectionLink}}" target="_blank">Consulter et signer les documents</a></strong></li>
        <li>Commentaire de l'auteur: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Einladung zur Unterzeichnung Ihres Dokuments',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Ein oder mehrere neue Dokumente wurden Ihrem Mietvertrag hinzugefügt. Um diese Dokumente zu prüfen und zu unterschreiben, laden wir Sie ein, sich mit dem untenstehenden Link in Ihrem Up2Rent-Konto anzumelden:</p>
        <ul>
        <li>Link: <strong><a href="{{connectionLink}}" target="_blank">Dokumente ansehen und unterschreiben</a></strong></li>
        <li>Kommentar des Autors: <strong>{{additionalMessage}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const newDocumenLinkMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: New link to sign your document',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>You have new documents awaiting your review and signature. Please use the link below to access them:</p>
        <ul>
        <li><strong><a href="{{connectionLink}}" target="_blank">Review and Sign Documents</a></strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Nieuwe link om uw document te ondertekenen',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Er zijn nieuwe documenten beschikbaar voor uw beoordeling en ondertekening. Gebruik de onderstaande link om toegang te krijgen:</p>
        <ul>
        <li><strong><a href="{{connectionLink}}" target="_blank">Documenten bekijken en ondertekenen</a></strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Nouveau lien pour signer votre document',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>De nouveaux documents sont disponibles pour votre relecture et signature. Veuillez utiliser le lien ci-dessous pour y accéder:</p>
        <ul>
        <li><strong><a href="{{connectionLink}}" target="_blank">Consulter et signer les documents</a></strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Neuer Link zum Signieren Ihres Dokuments',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Neue Dokumente stehen zur Überprüfung und Unterzeichnung bereit. Bitte nutzen Sie den folgenden Link, um darauf zuzugreifen:</p>
        <ul>
        <li><strong><a href="{{connectionLink}}" target="_blank">Dokumente ansehen und unterschreiben</a></strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const signedDocumentMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Documents are signed',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>One or more new documents have been signed by all parties.</p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Documenen zijn ondertekend',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Eén of meerdere documenten zijn ondertekend door alle partijen.</p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Invitation a signer votre document',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Un ou plusieurs nouveaux documents ont été signés par toutes les parties.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Dokumente sind unterzeichnet',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Ein oder mehrere neue Dokumente wurden von allen Parteien unterzeichnet.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const rejectedDocumentMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Document rejected',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>One or more documents have been rejected by one of the parties.</p>
        <ul>
        <li>Reason for rejection: <strong>{{rejectionReason}}</strong></li>
        </ul>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Document is afgewezen',
    body: `
        <p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Eén of meerdere documenten zijn afgewezen door één van de partijen.</p>
        <ul>
        <li>Reden voor afwijzing: <strong>{{rejectionReason}}</strong></li>
        </ul>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Document rejeté',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Un ou plusieurs documents ont été rejetés par l'une des parties.</p>
        <ul>
        <li>Raison du rejet: <strong>{{rejectionReason}}</strong></li>
        </ul>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Dokument abgelehnt',
    body: `<p>Sehr geehrte {{getContactName contact}},</p>
        <p></p>
        <p>Ein oder mehrere Dokumente wurden von einer der Parteien abgelehnt.</p>
        <ul>
        <li>Grund für die Ablehnung: <strong>{{rejectionReason}}</strong></li>
        </ul>
        ${SIGNATURES[Language.DE]}`,
  },
];

const retractedDocumentMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Document retracted',
    body: `<p>Dear {{getContactName contact}},</p>
        <p></p>
        <p>The document sent to you for signing on {{date}} has been retracted.</p>
        <p>For more information, please contact the author of the document.</p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Document is ingetrokken',
    body: `<p>Geachte {{getContactName contact}},</p>
        <p></p>
        <p>Het document dat op {{date}} naar u werd gestuurd voor ondertekening is ingetrokken.</p>
        <p>Voor meer informatie kunt u contact opnemen met de auteur van het document.</p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Document retiré',
    body: `<p>Cher/Chère {{getContactName contact}},</p>
        <p></p>
        <p>Le document qui vous a été envoyé pour signature le {{date}} a été retiré.</p>
        <p>Pour plus d'informations, veuillez contacter l'auteur du document.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Dokument zurückgezogen',
    body: `<p>Sehr geehrte/r {{getContactName contact}},</p>
        <p></p>
        <p>Das Dokument, das Ihnen am {{date}} zur Unterzeichnung gesendet wurde, wurde zurückgezogen.</p>
        <p>Für weitere Informationen wenden Sie sich bitte an den Autor des Dokuments.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const deleteMemberTemplate: MailTemplate[] = [
  {
    language: Language.EN,
    subject:
      'Up2Rent: Your invitation has been cancelled / Uw uitnodiging is geannuleerd / Votre invitation a été annulée',
    body: `<p>Dear Madam/Sir,</p>
    <p></p>
    <p>We are sorry to inform you that your invitation to join Up2Rent has been cancelled.</p>
    <p></p>
    <p>************** Nederlands hieronder **************</p>
    <p></p>
    <p>Mevrouw/Meneer,</p>
    <p></p>
    <p>Het spijt ons u te moeten meedelen dat uw uitnodiging om lid te worden van Up2Rent is geannuleerd.</p>
    <p></p>
    <p>************** Français ci dessous **************</p>
    <p></p>
    <p>Madame/Monsieur,</p>
    <p></p>
    <p>Nous sommes désolés de vous informer que votre invitation à rejoindre Up2Rent a été annulée.</p>
    <p></p>
    <p>************** Deutsch unten **************</p>
    <p></p>
    <p>Sehr geehrte Damen/Herren,</p>
    <p></p>
    <p>Wir bedauern, Ihnen mitteilen zu müssen, dass Ihre Einladung zur Teilnahme an Up2Rent storniert wurde.</p>
    ${SIGNATURES[Language.EN]}`,
  },
];

const deleteAlreadyRegisteredMemberTemplate: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Your account has been cancelled',
    body: `<p>Dear {{firstName}} {{lastName}},</p>
    <p></p>
    <p>The manager of your lease contract has deleted your personal data due to GDPR matters.</p>
    <p>This will delete your Up2Rent account at the same time.
    </p>
    ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Votre compte a été annulé',
    body: `<p>Cher/Chère {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Le gestionnaire de votre contrat de bail a supprimer vos données personnelles pour des questions RGPD.</p>
        <p>Ceci supprimera par la même occasion votre compte Up2Rent.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Uw account is geannuleerd',
    body: `
        <p>Geachte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>De beheerder van uw leasecontract heeft uw persoonlijke gegevens verwijderd vanwege de AVG.</p>
        <p>Dit zal ook uw Up2Rent account verwijderen.</p>
        <p></p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Ihr Konto wurde gelöscht',
    body: `<p>Sehr geehrte/r {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Der Verwalter Ihres Mietvertrags hat Ihre persönlichen Daten aufgrund von DSGVO-Bestimmungen gelöscht.</p>
        <p>Dies wird gleichzeitig Ihr Up2Rent-Konto löschen.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const changedPasswordMailTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Your email address has been changed',
    body: `${IMG_LOGO_CID_TAG}
    <p>Dear {{firstName}} {{lastName}},</p>
    <p></p>
    <p>Your email has been successfully updated to the address <strong>{{email}}</strong>.</p>
    <p>Please use this address to connect to Up2Rent from now on.</p>
    ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Votre adresse email a été modifée',
    body: `${IMG_LOGO_CID_TAG}
        <p>Cher/Chère {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Votre adresse mail a correctement été remplacée par <strong>{{email}}</strong>.</p>
        <p>Veuillez désormais utiliser cette adresse pour vous connecter à Up2Rent.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Uw email address wordt gewijzigd',
    body: `${IMG_LOGO_CID_TAG}
        <p>Geachte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Uw e-mail wordt met succes naar het adres <strong>{{email}}</strong> geüpdatet.</p>
        <p>Gebruik dit adress om verbinding te maken met Up2Rent vanaf nu.</p>
        <p></p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Ihre E-Mail-Adresse wurde geändert',
    body: `${IMG_LOGO_CID_TAG}
        <p>Sehr geehrte/r {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Ihre E-Mail-Adresse wurde erfolgreich auf <strong>{{email}}</strong> aktualisiert.</p>
        <p>Bitte verwenden Sie diese Adresse ab sofort, um sich bei Up2Rent anzumelden.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const newEmailVerificationTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Verify your new email address',
    body: `<p>Dear {{firstName}} {{lastName}},</p>
        <p></p>
        <p>This address is about to become the one you use to sign in to Up2Rent.</p>
        <p>To finish the change, please click the link below:</p>
        <p><a href="{{verificationLink}}">Verify my new email address</a></p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Vérifiez votre nouvelle adresse email',
    body: `<p>Cher/Chère {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Cette adresse est sur le point de devenir celle que vous utilisez pour vous connecter à Up2Rent.</p>
        <p>Pour finaliser le changement, veuillez cliquer sur le lien ci-dessous :</p>
        <p><a href="{{verificationLink}}">Vérifier ma nouvelle adresse email</a></p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Verifieer uw nieuwe e-mailadres',
    body: `<p>Geachte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Dit adres wordt binnenkort het adres waarmee u zich bij Up2Rent aanmeldt.</p>
        <p>Klik op de onderstaande link om de wijziging af te ronden:</p>
        <p><a href="{{verificationLink}}">Mijn nieuwe e-mailadres verifiëren</a></p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Bestätigen Sie Ihre neue E-Mail-Adresse',
    body: `<p>Sehr geehrte/r {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Diese Adresse wird in Kürze diejenige sein, mit der Sie sich bei Up2Rent anmelden.</p>
        <p>Um die Änderung abzuschließen, klicken Sie bitte auf den folgenden Link:</p>
        <p><a href="{{verificationLink}}">Meine neue E-Mail-Adresse bestätigen</a></p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const changeEmailConfirmationTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Confirm your new email address',
    body: `<p>Dear {{firstName}} {{lastName}},</p>
        <p></p>
        <p>We received a request to change the email address of your Up2Rent account to <strong>{{newEmail}}</strong>.</p>
        <p>To approve this change, please click the link below:</p>
        <p><a href="{{verificationLink}}">Confirm my new email address</a></p>
        <p>We will then send a verification link to {{newEmail}}. The change is complete once you have clicked that second link.</p>
        <p>If you did not ask for this change, simply ignore this email: your address stays unchanged.</p>
        ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Confirmez votre nouvelle adresse email',
    body: `<p>Cher/Chère {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Nous avons reçu une demande de modification de l'adresse email de votre compte Up2Rent vers <strong>{{newEmail}}</strong>.</p>
        <p>Pour approuver ce changement, veuillez cliquer sur le lien ci-dessous :</p>
        <p><a href="{{verificationLink}}">Confirmer ma nouvelle adresse email</a></p>
        <p>Nous enverrons ensuite un lien de vérification à {{newEmail}}. Le changement sera effectif une fois ce second lien cliqué.</p>
        <p>Si vous n'êtes pas à l'origine de cette demande, ignorez simplement cet email : votre adresse restera inchangée.</p>
        ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Bevestig uw nieuwe e-mailadres',
    body: `<p>Geachte {{firstName}} {{lastName}},</p>
        <p></p>
        <p>We hebben een verzoek ontvangen om het e-mailadres van uw Up2Rent-account te wijzigen naar <strong>{{newEmail}}</strong>.</p>
        <p>Klik op de onderstaande link om deze wijziging goed te keuren:</p>
        <p><a href="{{verificationLink}}">Mijn nieuwe e-mailadres bevestigen</a></p>
        <p>Daarna sturen we een verificatielink naar {{newEmail}}. De wijziging is voltooid zodra u op die tweede link hebt geklikt.</p>
        <p>Als u deze wijziging niet heeft aangevraagd, kunt u deze e-mail negeren: uw adres blijft ongewijzigd.</p>
        ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Bestätigen Sie Ihre neue E-Mail-Adresse',
    body: `<p>Sehr geehrte/r {{firstName}} {{lastName}},</p>
        <p></p>
        <p>Wir haben eine Anfrage erhalten, die E-Mail-Adresse Ihres Up2Rent-Kontos in <strong>{{newEmail}}</strong> zu ändern.</p>
        <p>Um diese Änderung zu genehmigen, klicken Sie bitte auf den folgenden Link:</p>
        <p><a href="{{verificationLink}}">Meine neue E-Mail-Adresse bestätigen</a></p>
        <p>Anschließend senden wir einen Bestätigungslink an {{newEmail}}. Die Änderung ist abgeschlossen, sobald Sie auf diesen zweiten Link geklickt haben.</p>
        <p>Falls Sie diese Änderung nicht angefordert haben, ignorieren Sie diese E-Mail: Ihre Adresse bleibt unverändert.</p>
        ${SIGNATURES[Language.DE]}`,
  },
];

const contactChangedByOwnerTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: One of your clients has changed his contact details',
    body: `${IMG_LOGO_CID_TAG}
      <p>Dear {{getContactName client}},</p>
      <p></p>
      <p>One of your clients has changed his contact details:</p>
      <ul>
      <li>Client/Owner: <strong>{{getContactName contact}}</strong></li>
      <li>Address: <strong>{{displayAddress contact.address language}}</strong></li>
      {{#if isDifferentEmail}}
      <li>Previous email: <strong>{{oldEmail}}</strong></li>
      <li>New email: <strong>{{email}}</strong></li>
      {{/if}}
      </ul>
      ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Un de vos clients a changé ses coordonnées',
    body: `${IMG_LOGO_CID_TAG}
      <p>Cher/Chère {{getContactName client}},</p>
      <p></p>
      <p>Un de vos clients a changé ses coordonnées:</p>
      <ul>
      <li>Client/Propriétaire: <strong>{{getContactName contact}}</strong></li>
      <li>Adresse: <strong>{{displayAddress contact.address language}}</strong></li>
      {{#if isDifferentEmail}}
      <li>Email précédent: <strong>{{oldEmail}}</strong></li>
      <li>Nouvel email: <strong>{{email}}</strong></li>
      {{/if}}
      </ul>
      ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Een van uw klanten heeft zijn contactgegevens veranderd',
    body: `${IMG_LOGO_CID_TAG}
      <p>Geachte {{getContactName client}},</p>
      <p></p>
      <p>Een van uw klanten heeft zijn contactgegevens veranderd:</p>
      <ul>
      <li>Klant/Eigenaar: <strong>{{getContactName contact}}</strong></li>
      <li>Adres: <strong>{{displayAddress contact.address language}}</strong></li>
      {{#if isDifferentEmail}}
      <li>Vorige email: <strong>{{oldEmail}}</strong></li>
      <li>Nieuwe email: <strong>{{email}}</strong></li>
      {{/if}}
      </ul>
      ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Einer Ihrer Kunden hat seine Kontaktdaten geändert',
    body: `${IMG_LOGO_CID_TAG}
      <p>Sehr geehrte/r {{getContactName client}},</p>
      <p></p>
      <p>Einer Ihrer Kunden hat seine Kontaktdaten geändert:</p>
      <ul>
      <li>Kunde/Eigentümer: <strong>{{getContactName contact}}</strong></li>
      <li>Adresse: <strong>{{displayAddress contact.address language}}</strong></li>
      {{#if isDifferentEmail}}
      <li>Vorherige E-Mail: <strong>{{oldEmail}}</strong></li>
      <li>Neue E-Mail: <strong>{{email}}</strong></li>
      {{/if}}
      </ul>
      ${SIGNATURES[Language.DE]}`,
  },
];

const activateSendingSourceReminderTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Only one day left to validate your domain name - ACTION REQUIRED',
    body: `<p>Dear {{getContactName contact}},</p>
      <p></p>
      <p>The domain <strong>{{ domainName }}</strong> has been added to your Up2Rent account.</p>
      <p>However, it seems that your DNS records have not been correctly updated yet and <strong>{{ domainName }}</strong> therefore cannot be verified. </p>
      <p></p>
      </p>We strongly advise you to check whether you correctly added the DNS records provided by Up2Rent (see “Sending email address” section in the Up2Rent settings)  to your domain’s DNS management service (this may be your domain registrar or a separate DNS service).</p>
      <p></p> 
      <p>Then, you can click the “Verify” button in the Up2Rent settings to check if the update has been done correctly (the update can take some time to appear).</p> 
      <p></p> 
      <p>For more information, please visit <a href="{{ sendingSourceSettingsUrl }}" target="_blank">your Up2Rent settings</a>.</p>
      <p></p>
      ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: `Up2Rent : Il ne reste qu\'un jour pour valider votre nom de domaine - ACTION REQUISE`,
    body: `<p>Cher/Chère {{getContactName contact}},</p>
      <p></p>
      <p>Le domaine <strong>{{ domainName }}</strong> a été ajouté à votre compte Up2Rent.</p>
      <p>Cependant, il semble que vos enregistrements DNS n'aient pas encore été correctement mis à jour et <strong>{{ domainName }}</strong> ne peut donc pas être vérifié.</p>
      <p></p>
      <p>Nous vous conseillons vivement de vérifier si vous avez correctement ajouté les enregistrements DNS fournis par Up2Rent (voir la section "Adresse email d'envoi" dans les paramètres Up2Rent) au service de gestion DNS de votre domaine (il peut s'agir de votre registraire de domaine ou d'un service DNS distinct).</p>
      <p></p>
      <p>Ensuite, vous pouvez cliquer sur le bouton "Vérifier" dans les paramètres de Up2Rent pour vérifier si la mise à jour a été effectuée correctement (la mise à jour peut prendre un certain temps à apparaître).</p> 
      <p></p>
      <p>Pour plus d'informations, veuillez consulter <a href="{{ sendingSourceSettingsUrl }}" target="_blank">vos paramètres Up2Rent</a>.</p>
      <p></p>
      ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: `Up2Rent: Nog één dag om uw domeinnaam te valideren - ACTIE VERPLICHT`,
    body: `<p>Geachte {{getContactName contact}},</p>
      <p></p>
      <p>Het domein <strong>{{ domainName }}</strong> is toegevoegd aan uw Up2Rent account.</p>
      <p>Het lijkt er echter op dat uw DNS-records nog niet goed zijn bijgewerkt en daarom kan <strong>{{ domainName }}</strong> niet worden geverifieerd.</p>
      <p></p>
      <p>Wij raden u sterk aan te controleren of u de door Up2Rent verstrekte DNS-records (zie de sectie "Emailadres voor verzendings" in de Up2Rent-instellingen) correct hebt toegevoegd aan de DNS-beheerservice voor uw domein (dit kan uw domeinregistrar of een aparte DNS-service zijn).</p>
      <p></p>
      <p>Daarna kun je in de instellingen van Up2Rent op de knop "Verifiëren" klikken om te controleren of de update correct is uitgevoerd (het kan even duren voordat de update verschijnt).</p> 
      <p></p>
      <p>Zie <a href="{{ sendingSourceSettingsUrl }}" target="_blank">het Up2Rent instellingenmenu</a> voor meer informatie.</p>
      <p></p>
      ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Nur noch ein Tag, um Ihren Domainnamen zu validieren - HANDLUNG ERFORDERLICH',
    body: `<p>Sehr geehrte/r {{getContactName contact}},</p>
      <p></p>
      <p>Die Domain <strong>{{ domainName }}</strong> wurde zu Ihrem Up2Rent-Konto hinzugefügt.</p>
      <p>Es scheint jedoch, dass Ihre DNS-Einträge noch nicht korrekt aktualisiert wurden und <strong>{{ domainName }}</strong> daher nicht verifiziert werden kann.</p>
      <p></p>
      <p>Wir empfehlen Ihnen dringend zu überprüfen, ob Sie die von Up2Rent bereitgestellten DNS-Einträge (siehe Abschnitt "E-Mail-Sendeadresse" in den Up2Rent-Einstellungen) korrekt zu Ihrem DNS-Verwaltungsdienst hinzugefügt haben (dies kann Ihr Domain-Registrar oder ein separater DNS-Dienst sein).</p>
      <p></p>
      <p>Anschließend können Sie in den Up2Rent-Einstellungen auf die Schaltfläche "Verifizieren" klicken, um zu prüfen, ob das Update korrekt durchgeführt wurde (die Aktualisierung kann einige Zeit in Anspruch nehmen).</p>
      <p></p>
      <p>Weitere Informationen finden Sie unter <a href="{{ sendingSourceSettingsUrl }}" target="_blank">Ihren Up2Rent-Einstellungen</a>.</p>
      <p></p>
      ${SIGNATURES[Language.DE]}`,
  },
];

const activateSendingSourceSuccessTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Congratulations! {{ domainName }} has been verified',
    body: `<p>Dear {{getContactName contact}},</p>
      <p></p>
      <p>The domain <strong>{{ domainName }}</strong> is correctly configured and has been verified.</p>
      <p></p>
      <p>From now on, if it is the first domain you have configured, emails generated by Up2Rent will be sent on your behalf from <strong>{{ emailAddress }}</strong>.</p>
      <p>If it is not the first domain configured, you have to make it default in the "Sending email address" section of your Up2Rent settings in order to send emails from {{ emailAddress }}.</p>
      <p></p>
      <p>You can update those settings through <a href="{{ sendingSourceSettingsUrl }}" target="_blank">your Up2Rent settings</a>.</p>
       <p></p>
      ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: 'Up2Rent: Félicitations ! {{ domainName }} a été vérifié',
    body: `<p> Cher/chère {{getContactName contact}},</p>
      <p></p>
      <p>Le domaine <strong>{{ domainName }}</strong> est correctement configuré et a été vérifié.</p>
      <p></p>
      <p>A partir de maintenant, s'il s'agit du premier domaine que vous ayiez configuré, les emails générés par Up2Rent seront envoyés en votre nom à partir de <strong>{{ emailAddress }}</strong>.</p>
      <p>Si ce n'est pas le premier domaine configuré, vous devez le rendre par défaut dans la section "Adresse email d'envoi" de vos paramètres Up2Rent afin d'envoyer des emails depuis {{ emailAddress }}.</p>
      <p></p>
      <p>Vous pouvez mettre à jour ces paramètres via <a href="{{ sendingSourceSettingsUrl }}" target="_blank">vos paramètres Up2Rent</a>.</p>
       <p></p>
      ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Gefeliciteerd! {{ domainName }} is geverifieerd',
    body: `<p> Geachte {{getContactName contact}},</p>
      <p></p>
      <p>Het <strong>{{ domainName }}</strong> domein is correct geconfigureerd en geverifieerd.</p>
      <p></p>
      <p>Vanaf nu, als dit het eerste domein is dat je hebt ingesteld, worden de e-mails die door Up2Rent worden gegenereerd in jouw naam verstuurd vanaf <strong>{{ emailAddress }}</strong>.</p>
      <p>Als dit niet het eerste domein is dat is geconfigureerd, moet u het als standaard instellen in het gedeelte "Emailadres voor verzendings" van uw Up2Rent instellingen om e-mails te kunnen verzenden vanaf {{ emailAddress }}.</p>
      <p></p>
      <p>Je kunt deze instellingen bijwerken via <a href="{{ sendingSourceSettingsUrl }}" target="_blank">het Up2Rent instellingenmenu</a>.</p>
      <p></p>
      ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Herzlichen Glückwunsch! {{ domainName }} wurde verifiziert',
    body: `<p>Sehr geehrte/r {{getContactName contact}},</p>
      <p></p>
      <p>Die Domain <strong>{{ domainName }}</strong> ist korrekt konfiguriert und wurde verifiziert.</p>
      <p></p>
      <p>Ab sofort werden, falls es sich um die erste von Ihnen konfigurierte Domain handelt, die von Up2Rent generierten E-Mails in Ihrem Namen von <strong>{{ emailAddress }}</strong> gesendet.</p>
      <p>Wenn es nicht die erste konfigurierte Domain ist, müssen Sie sie im Abschnitt "E-Mail-Sendeadresse" Ihrer Up2Rent-Einstellungen als Standard festlegen, um E-Mails von {{ emailAddress }} senden zu können.</p>
      <p></p>
      <p>Sie können diese Einstellungen über <a href="{{ sendingSourceSettingsUrl }}" target="_blank">Ihre Up2Rent-Einstellungen</a> aktualisieren.</p>
      <p></p>
      ${SIGNATURES[Language.DE]}`,
  },
];

const activateSendingSourceErrorTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Your domain name could not be validated - ACTION REQUIRED',
    body: `<p>Dear {{getContactName contact}},</p>
      <p></p>
      <p>The domain <strong>{{ domainName }}</strong> added to your Up2Rent account could not been verified.</p>
      <p></p>
      <p>For more information about this error, please visit <a href="{{ sendingSourceSettingsUrl }}" target="_blank"> your Up2Rent settings</a>.</p>
      <p></p>
      ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: "Up2Rent : Votre nom de domaine n'a pas pu être validé - ACTION REQUISE",
    body: `<p>Cher/Chère {{getContactName contact}},</p>
      <p></p>
      <p>Le domaine <strong>{{ domainName }}</strong> ajouté à votre compte Up2Rent n'a pas pu être vérifié.</p>
      <p></p>
      <p>Pour plus d'informations sur cette erreur, veuillez consulter <a href="{{ sendingSourceSettingsUrl }}" target="_blank"> vos paramètres Up2Rent</a>.</p>
      <p></p>
      ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent: Uw domeinnaam kon niet worden gevalideerd - ACTIE VERPLICHT',
    body: `<p>Geachte {{getContactName contact}},</p>
      <p></p>
      <p>Het domein <strong>{{ domainName }}</strong> toegevoegd aan uw Up2Rent account kon niet worden geverifieerd.</p>
      <p></p>
      <p>Zie <a href="{{ sendingSourceSettingsUrl }}" target="_blank">het Up2Rent instellingenmenu</a> voor meer informatie over deze fout.</p>
      <p></p>
      ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Ihr Domainname konnte nicht validiert werden - HANDLUNG ERFORDERLICH',
    body: `<p>Sehr geehrte/r {{getContactName contact}},</p>
      <p></p>
      <p>Die zu Ihrem Up2Rent-Konto hinzugefügte Domain <strong>{{ domainName }}</strong> konnte nicht verifiziert werden.</p>
      <p></p>
      <p>Weitere Informationen zu diesem Fehler finden Sie unter <a href="{{ sendingSourceSettingsUrl }}" target="_blank">Ihren Up2Rent-Einstellungen</a>.</p>
      <p></p>
      ${SIGNATURES[Language.DE]}`,
  },
];

const activateSendingSourceCrashTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Up2Rent: Your domain name is not verified anymore - ACTION REQUIRED',
    body: `<p>Dear {{getContactName contact}},</p>
      <p></p>
      <p>The domain <strong>{{ domainName }}</strong> is no longer verified. Emails generated by Up2Rent cannot be sent using this domain name anymore.</p>
      <p></p>
      <p>This problem may be caused by a change in your DNS configuration. </p>
      <p> In case you still want to use your domain, we strongly advise you to:
      <ol>
      <li>Delete and add again your domain in the Up2Rent settings (see "Sending email address" section in the Up2Rent settings).</li>
      <li>Make sure the DNS records provided by Up2Rent have not been altered or accidentally deleted at your domain’s DNS management service (this may be your domain registrar or a separate DNS service).</li>
      </ol>
      <p><p>
      <p> In case the ownership of the domain is not yours anymore or if you don’t want to use it anymore, we strongly advise to adapt your Up2Rent settings by deleting the unverified domain. You can either add a new domain or roll back to the by default no-reply@up2rent.com email address.</p>
      <p></p>
      <p>Warning: Without any action on your part, all new emails generated by Up2Rent will meanwhile remain in the "To be sent" status and will not reach their recipients. They will be sent as soon as a valid configuration has been defined.</p>
      <p></p>
      <p>For more information, please visit <a href="{{ sendingSourceSettingsUrl }}" target="_blank"> your Up2Rent settings</a>.</p>
      <p></p>
      ${SIGNATURES[Language.EN]}`,
  },

  {
    language: Language.FR,
    subject: "Up2Rent : Votre nom de domaine n'est plus vérifié - ACTION REQUISE",
    body: `<p>Cher/Chère {{getContactName contact}},</p>
      <p></p>
      <p>Le domaine <strong>{{ domainName }}</strong> n'est plus vérifié. Les courriels générés par Up2Rent ne peuvent plus être envoyés en utilisant ce nom de domaine.</p>
      <p></p>
      <p>Ce problème peut être causé par un changement dans votre configuration DNS. </p>
      <p>Dans le cas où vous souhaitez toujours utiliser votre domaine, nous vous conseillons vivement de : </p>
      <ol>
      <li>Supprimer et ajouter à nouveau votre domaine dans les paramètres de Up2Rent (voir la section "Adresse email d'envoi" dans les paramètres de Up2Rent).</li>
      <li>Vous assurer que les enregistrements DNS fournis par Up2Rent n'ont pas été modifiés ou supprimés accidentellement au niveau du service de gestion DNS de votre domaine (il peut s'agir de votre registraire de domaine ou d'un service DNS séparé).</li>
      </ol>
      <p><p>
      <p>Dans le cas où la propriété du domaine ne vous appartient plus ou si vous ne voulez plus l'utiliser, nous vous conseillons vivement d'adapter vos paramètres Up2Rent en supprimant le domaine non vérifié. Vous pouvez soit ajouter un nouveau domaine, soit revenir à l'adresse e-mail par défaut no-reply@up2rent.com.</p>
      <p></p>
      <p>Attention: sans aucune action de votre part, tous les nouveaux emails générés par Up2Rent resteront entre-temps dans le statut "A envoyer" et n'atteindront pas leurs destinataires. Ils seront envoyés dès qu'une configuration valide aura été définie.</p>
      <p></p>
      <p>Pour plus d'informations, veuillez consulter <a href="{{ sendingSourceSettingsUrl }}" target="_blank"> vos paramètres Up2Rent</a>.</p>
      <p></p>
      ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Up2Rent : Uw domeinnaam is niet langer geverifieerd - ACTIE VERPLICHT',
    body: `<p>Geachte {{getContactName contact}},</p>
      <p></p>
      <p>Het domein <strong>{{ domainName }}</strong> is niet langer geverifieerd. E-mails gegenereerd door Up2Rent kunnen niet langer verstuurd worden met deze domeinnaam.</p>
      <p></p>
      <p>Dit probleem kan worden veroorzaakt door een wijziging in uw DNS-configuratie.</p>
      <p>Indien u toch uw domein wilt gebruiken, raden wij u ten zeerste aan om : </p>
      <ol>
      <li>Verwijder uw domein en voeg het opnieuw toe in de Up2Rent instellingen (zie de "E-mailadres voor verzendings" sectie in Up2Rent instellingen).</li>
      <li>Zorg ervoor dat de door Up2Rent verstrekte DNS records niet per ongeluk zijn gewijzigd of verwijderd bij de DNS management service van je domein (dit kan je domein registrar zijn of een aparte DNS service).</li>
      </ol>
      <p><p>
      <p>Als het domein niet langer jouw eigendom is of je wilt het niet langer gebruiken, adviseren we je om je Up2Rent instellingen aan te passen door het niet-geverifieerde domein te verwijderen. U kunt een nieuw domein toevoegen of terugkeren naar het standaard e-mailadres no-reply@up2rent.com.</p>
      <p></p>
      <p>Waarschuwing: zonder enige actie van uw kant, zullen alle nieuwe e-mails die door Up2Rent worden gegenereerd in de "Te verzenden" status blijven en hun ontvangers niet bereiken. Ze worden verzonden zodra een geldige configuratie is gedefinieerd.</p>
      <p></p>
      <p>Zie <a href="{{ sendingSourceSettingsUrl }}" target="_blank">het Up2Rent instellingenmenu</a> voor meer informatie.</p>
      <p></p>
      ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Up2Rent: Ihr Domainname ist nicht mehr verifiziert - HANDLUNG ERFORDERLICH',
    body: `<p>Sehr geehrte/r {{getContactName contact}},</p>
      <p></p>
      <p>Die Domain <strong>{{ domainName }}</strong> ist nicht mehr verifiziert. E-Mails, die von Up2Rent generiert werden, können nicht mehr mit diesem Domainnamen gesendet werden.</p>
      <p></p>
      <p>Dieses Problem kann durch eine Änderung in Ihrer DNS-Konfiguration verursacht worden sein.</p>
      <p>Falls Sie Ihre Domain weiterhin nutzen möchten, empfehlen wir Ihnen dringend:</p>
      <ol>
      <li>Löschen und fügen Sie Ihre Domain in den Up2Rent-Einstellungen erneut hinzu (siehe Abschnitt "E-Mail-Sendeadresse" in den Up2Rent-Einstellungen).</li>
      <li>Stellen Sie sicher, dass die von Up2Rent bereitgestellten DNS-Einträge bei Ihrem DNS-Verwaltungsdienst nicht verändert oder versehentlich gelöscht wurden (dies kann Ihr Domain-Registrar oder ein separater DNS-Dienst sein).</li>
      </ol>
      <p></p>
      <p>Falls die Domain nicht mehr in Ihrem Besitz ist oder Sie sie nicht mehr verwenden möchten, empfehlen wir Ihnen dringend, Ihre Up2Rent-Einstellungen anzupassen, indem Sie die nicht verifizierte Domain löschen. Sie können entweder eine neue Domain hinzufügen oder zur Standard-E-Mail-Adresse no-reply@up2rent.com zurückkehren.</p>
      <p></p>
      <p>Warnung: Ohne Ihr Eingreifen werden alle neuen von Up2Rent generierten E-Mails im Status "Zu senden" verbleiben und ihre Empfänger nicht erreichen. Sie werden gesendet, sobald eine gültige Konfiguration definiert wurde.</p>
      <p></p>
      <p>Weitere Informationen finden Sie unter <a href="{{ sendingSourceSettingsUrl }}" target="_blank">Ihren Up2Rent-Einstellungen</a>.</p>
      <p></p>
      ${SIGNATURES[Language.DE]}`,
  },
];

const reportTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Real estate report',
    body: `
      <p>Dear {{getContactName contact}},</p>
      <p></p>
      <p>Please find your generated report attached.</p>
      <p></p>
      <p>Kind regards,</p>
      <p></p>
      ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: 'Votre rapport immobilier',
    body: `
      <p>Cher/Chère {{getContactName contact}},</p>
      <p></p>
      <p>Nous vous prions de prendre connaissance du nouveau rapport généré en pièce jointe.</p>
      <p></p>
      <p>Bien à vous,</p>
      <p></p>
      ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Uw vastgoed rapport',
    body: `
      <p>Geachte {{getContactName contact}},</p>
      <p></p>
      <p>In bijlage vindt u uw nieuw rapport.</p>
      <p></p>
      <p>Met vriendelijke groeten,</p>
      <p></p>
      ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Immobilienbericht',
    body: `
      <p>Sehr geehrte/r {{getContactName contact}},</p>
      <p></p>
      <p>Anbei finden Sie Ihren generierten Bericht.</p>
      <p></p>
      <p>Mit freundlichen Grüßen,</p>
      <p></p>
      ${SIGNATURES[Language.DE]}`,
  },
];

const accountingExportTemplates: MailTemplate[] = [
  {
    language: Language.EN,
    subject: 'Accounting export',
    body: `
      <p>Dear {{getContactName contact}},</p>
      <p></p>
      <p>Please find your exported accounting data attached.</p>
      <p></p>
      <p>Kind regards,</p>
      <p></p>
      ${SIGNATURES[Language.EN]}`,
  },
  {
    language: Language.FR,
    subject: 'Export Comptable',
    body: `
      <p>Cher/Chère {{getContactName contact}},</p>
      <p></p>
      <p>Veuillez trouver vos données comptables exportées en pièce jointe.</p>
      <p></p>
      <p>Bien à vous,</p>
      <p></p>
      ${SIGNATURES[Language.FR]}`,
  },
  {
    language: Language.NL,
    subject: 'Boekhoudkundige export',
    body: `
      <p>Geachte {{getContactName contact}},</p>
      <p></p>
      <p>In de bijlage vindt u uw boekhoudkundige gegevens.</p>
      <p></p>
      <p>Met vriendelijke groeten,</p>
      <p></p>
      ${SIGNATURES[Language.NL]}`,
  },
  {
    language: Language.DE,
    subject: 'Buchhaltungsexport',
    body: `
      <p>Sehr geehrte/r {{getContactName contact}},</p>
      <p></p>
      <p>Anbei finden Sie Ihre exportierten Buchhaltungsdaten.</p>
      <p></p>
      <p>Mit freundlichen Grüßen,</p>
      <p></p>
      ${SIGNATURES[Language.DE]}`,
  },
];

export enum FixedTemplateType {
  NEW_ACCOUNT,
  SIGN_UP,
  FORGOT_PASSWORD,
  RESEND_VERIFICATION_CODE,
  INVITE_MEMBER_TO_ACCOUNT,
  INVITE_OWNER_TO_ACCOUNT,
  INVITE_TENANT_TO_ACCOUNT,
  INVITE_CONTACT_TO_SIGN_LEASE,
  INVITE_CONTACT_TO_ACCOUNT_FOR_DOCUMENT,
  NEW_LEASE_VERSION,
  NEW_DOCUMENT,
  DELETE_MEMBER,
  DELETE_ALREADY_REGISTERED_MEMBER,
  CHANGE_PASSWORD,
  CHANGE_EMAIL_CONFIRMATION,
  CONTACT_CHANGED_BY_OWNER,
  ACTIVATE_SENDING_SOURCE_REMINDER,
  ACTIVATE_SENDING_SOURCE_SUCCESS,
  ACTIVATE_SENDING_SOURCE_ERROR,
  ACTIVATE_SENDING_SOURCE_CRASH,
  REPORT,
  ACCOUNTING_EXPORT,
  DOCUMENT_SIGNED,
  NEW_DOCUMENT_LINK,
  DOCUMENT_RETRACTED,
  DOCUMENT_REJECTED,
  REMINDER_DOCUMENT_SIGNATURE,
  REMINDER_INVITE_MEMBER_TO_ACCOUNT,
  REMINDER_INVITE_OWNER_TO_ACCOUNT,
  REMINDER_INVITE_TENANT_TO_ACCOUNT,
  REMINDER_INVITE_CONTACT_TO_SIGN_LEASE,
  REMINDER_INVITE_CONTACT_TO_ACCOUNT_FOR_DOCUMENT,
  SIGN_IN_MAGIC_LINK,
  ACCOUNT_DELETION_REQUESTED,
  OWNERSHIP_RECEIVED,
  OWNERSHIP_GIVEN,
  NEW_EMAIL_VERIFICATION,
}

// INTERNAL TYPES (sent from U2R and not from custom domain)
export const INTERNAL_FIXED_TEMPLATE_TYPE = [
  FixedTemplateType.NEW_ACCOUNT,
  FixedTemplateType.SIGN_UP,
  FixedTemplateType.FORGOT_PASSWORD,
  FixedTemplateType.RESEND_VERIFICATION_CODE,
  FixedTemplateType.INVITE_MEMBER_TO_ACCOUNT,
  FixedTemplateType.DELETE_MEMBER,
  FixedTemplateType.DELETE_ALREADY_REGISTERED_MEMBER,
  FixedTemplateType.CHANGE_PASSWORD,
  FixedTemplateType.CHANGE_EMAIL_CONFIRMATION,
  FixedTemplateType.NEW_EMAIL_VERIFICATION,
  FixedTemplateType.CONTACT_CHANGED_BY_OWNER,
  FixedTemplateType.ACTIVATE_SENDING_SOURCE_REMINDER,
  FixedTemplateType.ACTIVATE_SENDING_SOURCE_SUCCESS,
  FixedTemplateType.ACTIVATE_SENDING_SOURCE_ERROR,
  FixedTemplateType.ACTIVATE_SENDING_SOURCE_CRASH,
  FixedTemplateType.REPORT,
  FixedTemplateType.ACCOUNTING_EXPORT,
  FixedTemplateType.SIGN_IN_MAGIC_LINK,
  FixedTemplateType.ACCOUNT_DELETION_REQUESTED,
  FixedTemplateType.OWNERSHIP_RECEIVED,
  FixedTemplateType.OWNERSHIP_GIVEN,
];

export const getFixedMailTemplate = (type: FixedTemplateType, language: string = DEFAULT_LANGUAGE) => {
  switch (type) {
    case FixedTemplateType.NEW_ACCOUNT:
      return getMailTemplateForLanguage(language, linkAccountMailTemplates);
    case FixedTemplateType.SIGN_UP:
      return getMailTemplateForLanguage(language, signupMailTemplates);
    case FixedTemplateType.FORGOT_PASSWORD:
      return getMailTemplateForLanguage(language, forgotPasswordMailTemplates);
    case FixedTemplateType.RESEND_VERIFICATION_CODE:
      return getMailTemplateForLanguage(language, resendVerificationCodeMailTemplates);
    case FixedTemplateType.INVITE_MEMBER_TO_ACCOUNT:
      return getMailTemplateForLanguage(language, inviteMemberToAccountMailTemplates);
    case FixedTemplateType.INVITE_OWNER_TO_ACCOUNT:
      return getMailTemplateForLanguage(language, inviteOwnerToAccountMailTemplates);
    case FixedTemplateType.INVITE_TENANT_TO_ACCOUNT:
      return getMailTemplateForLanguage(language, inviteTenantToAccountMailTemplates);
    case FixedTemplateType.INVITE_CONTACT_TO_SIGN_LEASE:
      return getMailTemplateForLanguage(language, inviteContactToSignLeaseMailTemplates);
    case FixedTemplateType.INVITE_CONTACT_TO_ACCOUNT_FOR_DOCUMENT:
      return getMailTemplateForLanguage(language, inviteContactToAccountForDocumentMailTemplates);
    case FixedTemplateType.NEW_LEASE_VERSION:
      return getMailTemplateForLanguage(language, newLeaseVersionMailTemplates);
    case FixedTemplateType.NEW_DOCUMENT:
      return getMailTemplateForLanguage(language, newDocumentMailTemplates);
    case FixedTemplateType.NEW_DOCUMENT_LINK:
      return getMailTemplateForLanguage(language, newDocumenLinkMailTemplates);
    case FixedTemplateType.DELETE_MEMBER:
      return getMailTemplateForLanguage(language, deleteMemberTemplate);
    case FixedTemplateType.DELETE_ALREADY_REGISTERED_MEMBER:
      return getMailTemplateForLanguage(language, deleteAlreadyRegisteredMemberTemplate);
    case FixedTemplateType.CHANGE_PASSWORD:
      return getMailTemplateForLanguage(language, changedPasswordMailTemplates);
    case FixedTemplateType.CHANGE_EMAIL_CONFIRMATION:
      return getMailTemplateForLanguage(language, changeEmailConfirmationTemplates);
    case FixedTemplateType.NEW_EMAIL_VERIFICATION:
      return getMailTemplateForLanguage(language, newEmailVerificationTemplates);
    case FixedTemplateType.CONTACT_CHANGED_BY_OWNER:
      return getMailTemplateForLanguage(language, contactChangedByOwnerTemplates);
    case FixedTemplateType.ACTIVATE_SENDING_SOURCE_REMINDER:
      return getMailTemplateForLanguage(language, activateSendingSourceReminderTemplates);
    case FixedTemplateType.ACTIVATE_SENDING_SOURCE_SUCCESS:
      return getMailTemplateForLanguage(language, activateSendingSourceSuccessTemplates);
    case FixedTemplateType.ACTIVATE_SENDING_SOURCE_ERROR:
      return getMailTemplateForLanguage(language, activateSendingSourceErrorTemplates);
    case FixedTemplateType.ACTIVATE_SENDING_SOURCE_CRASH:
      return getMailTemplateForLanguage(language, activateSendingSourceCrashTemplates);
    case FixedTemplateType.REPORT:
      return getMailTemplateForLanguage(language, reportTemplates);
    case FixedTemplateType.ACCOUNTING_EXPORT:
      return getMailTemplateForLanguage(language, accountingExportTemplates);
    case FixedTemplateType.DOCUMENT_SIGNED:
      return getMailTemplateForLanguage(language, signedDocumentMailTemplates);
    case FixedTemplateType.DOCUMENT_REJECTED:
      return getMailTemplateForLanguage(language, rejectedDocumentMailTemplates);
    case FixedTemplateType.DOCUMENT_RETRACTED:
      return getMailTemplateForLanguage(language, retractedDocumentMailTemplates);
    case FixedTemplateType.REMINDER_DOCUMENT_SIGNATURE:
      return getMailTemplateForLanguage(language, documentSignatureReminderMailTemplates);
    case FixedTemplateType.REMINDER_INVITE_MEMBER_TO_ACCOUNT:
      return getMailTemplateForLanguage(language, reminderInviteMemberToAccountMailTemplates);
    case FixedTemplateType.REMINDER_INVITE_OWNER_TO_ACCOUNT:
      return getMailTemplateForLanguage(language, reminderInviteOwnerToAccountMailTemplates);
    case FixedTemplateType.REMINDER_INVITE_TENANT_TO_ACCOUNT:
      return getMailTemplateForLanguage(language, reminderInviteTenantToAccountMailTemplates);
    case FixedTemplateType.REMINDER_INVITE_CONTACT_TO_SIGN_LEASE:
      return getMailTemplateForLanguage(language, reminderInviteContactToSignLeaseMailTemplates);
    case FixedTemplateType.REMINDER_INVITE_CONTACT_TO_ACCOUNT_FOR_DOCUMENT:
      return getMailTemplateForLanguage(language, reminderInviteContactToAccountForDocumentMailTemplates);
    case FixedTemplateType.SIGN_IN_MAGIC_LINK:
      return getMailTemplateForLanguage(language, signInMagicLinkMailTemplates);
    case FixedTemplateType.ACCOUNT_DELETION_REQUESTED:
      return getMailTemplateForLanguage(language, accountDeletionRequestedMailTemplates);
    case FixedTemplateType.OWNERSHIP_RECEIVED:
      return getMailTemplateForLanguage(language, ownershipReceivedMailTemplates);
    case FixedTemplateType.OWNERSHIP_GIVEN:
      return getMailTemplateForLanguage(language, ownershipGivenMailTemplates);
    default:
      throw new Error(`${type} is not a template type`);
  }
};

const getMailTemplateForLanguage = (language: string, templateList: MailTemplate[]): MailTemplate => {
  if (isNil(language) || isEmpty(language)) {
    language = DEFAULT_LANGUAGE;
  }
  language = toUpper(language);

  let template = templateList.find((emailTemplate) => emailTemplate.language === language);
  if (isNil(template)) {
    template = templateList.find((defaultTemplate) => defaultTemplate.language === DEFAULT_LANGUAGE);
  }
  return template!;
};
